import React from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/ui/datatable";
import { auditsColumnConfig } from "@/config/tableColumnConfig";
import type { Audit } from "@/app/pages/auditcentre/index";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { AuditCreateDialog } from "./AuditCreateDialog";
import { useTenancy } from "@/contexts/TenancyContext";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { AuditService } from "@/app/pages/auditcentre/auditService";

// --- In-table editing state ---
import { AuditEditDialog } from "./AuditEditDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

type AuditTableProps = {
    audits: Audit[];
    isCreateDialogOpen: boolean;
    setIsCreateDialogOpen: (open: boolean) => void;
    handleCreateAudit: (
        audit: Omit<Audit, 'id' | 'findings' | 'incidents'> & {
            findings: Array<Omit<Audit['findings'][number], 'id'>>;
            incidents: Array<Omit<Audit['incidents'][number], 'id'>>;
        }
    ) => void | string | Promise<void | string>;
    auditService: AuditService | null;
    onDelete?: (id: string) => void;
    onEdit?: (audit: Audit) => void;
};

const OwnersCell: React.FC<{ owners: Audit["owners"] }> = ({ owners }) => {
    const { userIdToName } = useTenancy();
    const asArray = Array.isArray(owners)
        ? owners.filter(Boolean)
        : owners
            ? [owners]
            : [];

    if (asArray.length === 0) {
        return <span>Unassigned</span>;
    }

    return <span>{asArray.map(owner => userIdToName[owner] || owner).join(", ")}</span>;
};

const AuditActionsCell: React.FC<{ audit: Audit; onEdit?: (audit: Audit) => void; onDelete?: (id: string) => void }> = ({ audit, onEdit, onDelete }) => {
    const navigate = useNavigate();
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-gray-500">Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/auditcentre/${audit.id}`);
                }}>
                    View
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    onEdit && onEdit(audit);
                }}>
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete && onDelete(audit.id);
                    }}
                    className="text-red-600"
                >
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const auditColumns = (onEdit?: (audit: Audit) => void, onDelete?: (id: string) => void): ColumnDef<Audit>[] => [
    {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => <span>{row.original.type}</span>,
    },
    {
        // DED-1141: Changed from 'Scope' to 'Audit Scope'
        accessorKey: "scope",
        header: "Audit Scope",
        cell: ({ row }) => <span>{row.original.scope}</span>,
    },
    {
        accessorKey: "owners",
        header: "Owners",
        cell: ({ row }) => <OwnersCell owners={row.original.owners} />, 
    },
    {
        accessorKey: "auditor",
        header: "Auditor",
        cell: ({ row }) => <span>{row.original.auditor}</span>,
    },
    {
        id: "actions",
        header: "Actions",
        meta: { isAction: true },
        cell: ({ row }) => (
            <AuditActionsCell
                audit={row.original}
                onEdit={onEdit}
                onDelete={onDelete}
            />
        ),
    },
];

export function AuditTable({ audits, isCreateDialogOpen, setIsCreateDialogOpen, handleCreateAudit, auditService, onDelete, onEdit }: AuditTableProps) {
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const [auditToDelete, setAuditToDelete] = React.useState<string | null>(null);

    const handleDeletePrompt = (id: string) => {
        setAuditToDelete(id);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = () => {
        if (auditToDelete && onDelete) {
            onDelete(auditToDelete);
        }
        setShowDeleteDialog(false);
        setAuditToDelete(null);
    };

    const handleDeleteCancel = () => {
        setShowDeleteDialog(false);
        setAuditToDelete(null);
    };

    const navigate = useNavigate();
    return (
        <>
            <AuditCreateDialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
                onCreate={handleCreateAudit}
                auditService={auditService!}
            />
            <Button onClick={() => setIsCreateDialogOpen(true)}>Create Audit</Button>
            <DataTable columns={auditColumns(onEdit, handleDeletePrompt)} data={audits} pageSize={5} columnDisplayConfig={auditsColumnConfig} onRowClick={(row) => { navigate(`/auditcentre/${row.id}`) }} />

            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="bg-card">
                    <DialogHeader>
                        <DialogTitle>Delete Audit</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this audit? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={handleDeleteCancel}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteConfirm}>
                            Delete
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
